import { useState } from 'react';

import { DateTime } from 'luxon';
import type { DateRange } from 'react-day-picker';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';

import { Button } from '../../Button/Button';
import { Popover } from '../../Popover/Popover';

import { BrowserJSDateToOrgLuxonDateTime } from '../../../utils/date/BrowserJSDateToOrgLuxonDateTime';
import { OrgLuxonDateTimeToBrowserJSDate } from '../../../utils/date/OrgLuxonDateTimeToBrowserJSDate';

export type { DateRange };

const MIN_JS_DATE = new Date(-8640000000000000);
const MAX_JS_DATE = new Date(8640000000000000);

const customStyles = `
  .rdp-root {
    --rdp-accent-color: #0CACA6;
    --rdp-accent-background-color: rgba(12, 172, 166, 0.15);
  }
`;

interface DatePickerProps {
  triggerElement: React.ReactNode;
  value?: DateTime;
  onChange: (date: DateTime | undefined) => void;
  maxDate?: DateTime;
  minDate?: DateTime;
  timezone?: string;
  open?: boolean;
  onClose?: () => void;
}

export function DatePicker({
  triggerElement,
  value,
  onChange,
  maxDate,
  minDate,
  timezone,
  open = false,
  onClose,
}: DatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const openStateManagedInternally = onClose ? false : true;

  const handleOpenChange = (open: boolean) => {
    // Managed externally
    if (!openStateManagedInternally) {
      onClose?.();
      return;
    }
    // Managed internally
    setIsOpen(open);
  };

  const pickerZone = timezone ?? DateTime.local().zoneName;

  const convertToJSDate = (date?: DateTime) =>
    date ? OrgLuxonDateTimeToBrowserJSDate(date) : undefined;

  const valueAsJSDate = convertToJSDate(value);
  const minDateAsJSDate = convertToJSDate(minDate) || MIN_JS_DATE;
  const maxDateAsJSDate = convertToJSDate(maxDate) || MAX_JS_DATE;
  const endMonth = DateTime.now()
    .plus({ years: 5 })
    .set({ month: 12 })
    .toJSDate();

  const handleDateSelection = (day: Date) => {
    const luxonDateTime = BrowserJSDateToOrgLuxonDateTime(day, pickerZone);
    onChange(luxonDateTime);
    handleOpenChange(false);
  };

  const handleQuickSelection = (daysToAdd: number) => () => {
    const selectedDate = DateTime.now().plus({ days: daysToAdd });
    onChange(selectedDate);
    handleOpenChange(false);
  };

  return (
    <>
      <style>{customStyles}</style>
      <Popover
        trigger={triggerElement}
        open={openStateManagedInternally ? isOpen : open}
        onOpenChange={handleOpenChange}
        content={
          <>
            <DayPicker
              mode="single"
              timeZone={pickerZone}
              selected={valueAsJSDate}
              captionLayout="dropdown"
              endMonth={endMonth}
              onDayClick={handleDateSelection}
              disabled={{ before: minDateAsJSDate, after: maxDateAsJSDate }}
              today={OrgLuxonDateTimeToBrowserJSDate(DateTime.local())}
            />
            <div className="flex justify-between px-3 pt-2">
              <Button
                dataTestId="date-picker-today-btn"
                onClick={handleQuickSelection(0)}
                variant="ghost"
                className="hover:text-mint"
              >
                Today
              </Button>
              <Button
                dataTestId="date-picker-tomorrow-btn"
                onClick={handleQuickSelection(1)}
                variant="ghost"
                className="hover:text-mint"
              >
                Tomorrow
              </Button>
            </div>
          </>
        }
      />
    </>
  );
}
